import { useEffect, useMemo, useState } from 'react'
import { Select } from 'antd'
import type { SelectProps } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import apiClient from '../api/client'

interface CustomerHit {
  id: number
  customer_no: string
  company?: string | null
  first_name?: string | null
  last_name?: string | null
  email?: string | null
}

const labelOf = (c: CustomerHit) => {
  const person = [c.first_name, c.last_name].filter(Boolean).join(' ')
  return `${c.customer_no} — ${c.company || person || c.email || '?'}`
}

interface Props extends Omit<SelectProps<number>, 'options' | 'showSearch' | 'filterOption' | 'onSearch' | 'loading'> {
  // Wird fuer die Suche mitgesendet (z.B. nur aktive Kunden)
  extraParams?: Record<string, string>
}

// CustomerSelect searches customers on the server (?q=, matches customer_no,
// e-mail, last name, company) instead of preloading a fixed first page — with a
// preloaded list, customers beyond the limit simply could not be selected.
// Works as a Form.Item child (value / onChange).
export default function CustomerSelect({ extraParams, value, ...rest }: Props) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(id)
  }, [search])

  const hitsQ = useQuery({
    queryKey: ['customer-select', debounced, extraParams],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: CustomerHit[] } }>('/api/v1/customers', {
        params: { q: debounced || undefined, limit: 30, ...extraParams },
      })
      return res.data.data.items ?? []
    },
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  })

  const hits = hitsQ.data ?? []
  const known = value != null && hits.some(c => c.id === value)

  // A preset/current value that is not on the visible result page still needs
  // its label — fetch that one customer.
  const currentQ = useQuery({
    enabled: value != null && !known,
    queryKey: ['customer-select-one', value],
    queryFn: async () => {
      const res = await apiClient.get<{ data: CustomerHit }>(`/api/v1/customers/${value}`)
      return res.data.data
    },
    staleTime: 5 * 60_000,
  })

  const options = useMemo(() => {
    const out = hits.map(c => ({ value: c.id, label: labelOf(c) }))
    if (value != null && !known && currentQ.data) out.unshift({ value: currentQ.data.id, label: labelOf(currentQ.data) })
    return out
  }, [hits, value, known, currentQ.data])

  return (
    <Select<number>
      showSearch
      allowClear
      filterOption={false}
      onSearch={setSearch}
      loading={hitsQ.isFetching}
      options={options}
      value={value}
      placeholder={t('common.customerSearch', 'Kunde suchen (Nr., Name, E-Mail)')}
      notFoundContent={hitsQ.isFetching ? undefined : t('common.noResults', 'Keine Treffer')}
      {...rest}
    />
  )
}
